import { v4 as uuidv4 } from 'uuid'
import { Checkout } from '@afterpaytouch/portal-api/types'
import { connectBridge } from '@afterpaytouch/bridge/child'
import { MessageProtocol } from '../model'
import { useCheckoutState } from '../state'
import { LogEvent, TrackingEvent } from '../model/monitoring'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../integrations/monitoring'

// eslint-disable-next-line
export type Callback = (payload: any) => Promise<any>

type CheckoutXprops = Window['xprops'] & {
  onShippingOptionChange: Callback
  onMessage: Callback
  endTransaction: Callback
  onShippingAddressChange: Callback
  postMessageToInAppBrowser?: Callback
  onBrandUpdate: Callback
}

const xprops = (): CheckoutXprops => window.xprops as CheckoutXprops

const SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE'
const MERCHANT_TIMEOUT = 'MERCHANT_TIMEOUT'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let zoidComponent: any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazilyImportZoid = async (zoidVersion: string): Promise<any> => {
  switch (zoidVersion) {
    case '9.0.54':
      return await import('zoid_9-0-54')
    case '9.0.85':
      return await import('zoid_9-0-85')
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const initialiseZoid = async (zoidVersion: string): Promise<any> => {
  const useBridge = new URLSearchParams(window.location.search).has('bridge')

  if (useBridge) {
    await connectBridge({ tag: 'afterpay-checkout' })
    if (!('xprops' in window)) {
      console.error('Bridge failed to populate xprops.')
    }
    return
  }

  if (zoidComponent === undefined) {
    const zoid = await lazilyImportZoid(zoidVersion)
    zoidComponent = zoid.create({
      tag: 'afterpay-checkout',
    })
  }

  return zoidComponent
}

type MessageType = 'onMessage' | 'endTransaction' | 'onShippingAddressChange' | 'onShippingOptionChange' | 'onBrandUpdate'

interface MerchantCallback {
  endTransaction: Callback
  sendMessage: Callback
  onShippingOptionChange: Callback
  onShippingAddressChange: Callback
  updateBrand: Callback
}

export const getPopupOrigin = (checkout?: Checkout): string | null => {
  const regex = /^https?:\/\/[^/]+/i
  const popupOrigin = checkout?.merchant.popupOriginUrl?.match(regex)?.[0]
  const fallback = checkout?.merchant.callbackUrl?.match(regex)?.[0]
  return popupOrigin ?? fallback ?? null
}

type CallbackBuilder = (name: MessageType, timeout?: number) => Callback

const getCallbackMethod = (protocol: MessageProtocol, logEvent: LogEvent, origin?: string | null, zoidVersion: string = '9.0.54'): CallbackBuilder => {
  switch (protocol) {
    case 'json':
      return (type, timeout: number = 2000): Callback =>
        async (payload) =>
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          await sendMessage(type, payload, timeout, origin, logEvent)
    case 'legacy':
      return (type): Callback =>
        type === 'endTransaction'
          ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
            async (payload) => await sendLegacyMessage(payload, origin, logEvent)
          : async () => await Promise.reject(new Error('Not available with legacy afterpay.js'))
    case 'zoid':
      return (type, timeout: number = 2000): Callback =>
        async (payload) =>
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          await sendZoidMessage(type, payload, timeout, logEvent, zoidVersion)
  }
}

export const useMerchantCallback = (): MerchantCallback => {
  const { initial, checkout } = useCheckoutState()
  const popupOriginUrl = getPopupOrigin(checkout)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const send = getCallbackMethod(initial?.protocol ?? 'legacy', logEvent, popupOriginUrl, initial?.zoid)

  return {
    endTransaction: send('endTransaction', 5000),
    sendMessage: send('onMessage'),
    onShippingAddressChange: send('onShippingAddressChange', 15000),
    onShippingOptionChange: send('onShippingOptionChange', 7000),
    updateBrand: send('onBrandUpdate', 5000),
  }
}

async function sendLegacyMessage(payload: unknown, popupOrigin: null | string = '*', logEvent: LogEvent): Promise<void> {
  const eventProps = {
    message: JSON.stringify(payload),
    messageSentTo: popupOrigin,
    windowOpenerAvailable: !(window.opener === null || typeof window.opener === 'undefined'),
  }
  if (window.opener === null || typeof window.opener === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    reportWindowOpenerError(undefined, payload)
    throw new Error(SERVICE_UNAVAILABLE)
  }
  window.opener.postMessage(JSON.stringify(payload), popupOrigin)
  logEvent(TrackingEvent.POST_MESSAGE_SENT, eventProps)
}

interface Message {
  type: MessageType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  meta: {
    requestId: string
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function sendMessage(type: MessageType, payload: any, timeout: number, popupOrigin: null | string = '*', logEvent: LogEvent): Promise<any> {
  const eventProps = {
    messageType: type,
    messageSentTo: popupOrigin,
    windowOpenerAvailable: !(window.opener === null || typeof window.opener === 'undefined'),
  }

  if (window.opener === null || typeof window.opener === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    reportWindowOpenerError(type, payload)
    logEvent(TrackingEvent.POST_MESSAGE_UNAVAILABLE, { ...eventProps, errorType: SERVICE_UNAVAILABLE })
    throw new Error(SERVICE_UNAVAILABLE)
  }
  return await new Promise((resolve, reject) => {
    const requestId = uuidv4()
    const listener = (event: MessageEvent): void => {
      if (popupOrigin !== event.origin) {
        return
      }
      if (event.data?.meta?.requestId !== requestId) {
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      clearTimeout(errorTimer)
      removeEventListener('message', listener, false)

      if (event.data?.error !== undefined) {
        logEvent(TrackingEvent.POST_MESSAGE_ERROR, { ...eventProps, errorType: event.data })
        reject(new Error(event.data.error))
      } else {
        resolve(event.data?.payload)
      }
    }
    const errorTimer = setTimeout(() => {
      removeEventListener('message', listener, false)
      sendMessage(
        'onMessage',
        {
          severity: 'error',
          message: `Response is needed within ${timeout}ms`,
        },
        30000,
        popupOrigin,
        logEvent
      ).catch(console.warn)
      logEvent(TrackingEvent.POST_MESSAGE_ERROR, { ...eventProps, errorType: MERCHANT_TIMEOUT })
      reject(new Error(MERCHANT_TIMEOUT))
    }, timeout)

    const message: Message = {
      type,
      payload,
      meta: {
        requestId,
      },
    }
    addEventListener('message', listener, false)

    window.opener.postMessage(message, popupOrigin)
    logEvent(TrackingEvent.POST_MESSAGE_SENT, { ...eventProps })
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function sendZoidMessage(type: MessageType, payload: any, timeout: number, logEvent: LogEvent, zoidVersion: string): Promise<any> {
  const eventProps = {
    messageType: type,
  }

  if (typeof window.xprops === 'undefined') {
    await initialiseZoid(zoidVersion)
    logEvent('Zoid initialized', {
      zoidVersion,
    })
  }

  return await new Promise((resolve, reject) => {
    const errorTimer = window.setTimeout(() => {
      xprops()
        .onMessage?.({
          severity: 'error',
          message: `Response is needed within ${timeout}ms`,
        })
        ?.catch(console.warn)
      logEvent(TrackingEvent.ZOID_MESSAGE_ERROR, { ...eventProps, errorType: MERCHANT_TIMEOUT })

      reject(new Error(MERCHANT_TIMEOUT))
    }, timeout)

    logEvent(TrackingEvent.ZOID_MESSAGE_SENT, { ...eventProps })

    Promise.resolve(xprops()[type](payload))
      .then(
        (value) => {
          resolve(value)
        },
        (err) => {
          logEvent(TrackingEvent.ZOID_MESSAGE_ERROR, { ...eventProps, errorType: err?.message ?? err })
          reject(err)
        }
      )
      .finally(() => window.clearTimeout(errorTimer))
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function reportWindowOpenerError(type?: MessageType, payload?: any): void {
  console.error('Unable to sendMessage as window.opener not present')
  capture.error('Unable to sendMessage as window.opener not present', {
    'Message type': type,
    'Message payload': payload,
  })
}

export function generateMerchantNameAbbr(name?: string | null): string {
  if (name === null || name === undefined || name.length === 0) {
    return ''
  }

  const cleanedName = name.replace(/[^A-Za-z0-9\s]/g, '')

  const words = cleanedName.split(/\s+/).filter(Boolean)

  if (words.length === 1) {
    return words[0].substring(0, 2)
  }

  if (/\d+/.test(words[0])) {
    if (words.length > 2) {
      return words[1][0] + words[2][0]
    }
    return words[1][0] + words[1][1]
  }
  return words[0][0] + words[1][0]
}
